import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Sparkles } from 'lucide-react'

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'very-low' | 'none'

interface ConfidenceBadgeProps {
  score?: number | null
  showPercentage?: boolean
  showIcon?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * Get confidence level based on score
 * - 90-100%: High (green) - Auto-approved
 * - 70-89%: Medium (yellow) - Suggested, user confirms
 * - 50-69%: Low (orange) - Needs review
 * - 0-49%: Very Low (red) - Needs manual decision
 */
export function getConfidenceLevel(score?: number | null): ConfidenceLevel {
  if (score === null || score === undefined) return 'none'
  // Score is stored as 0-100 in backend
  const percentage = score > 1 ? score : score * 100
  if (percentage >= 90) return 'high'
  if (percentage >= 70) return 'medium'
  if (percentage >= 50) return 'low'
  return 'very-low'
}

const CONFIDENCE_CONFIG: Record<ConfidenceLevel, { label: string; color: string; bgColor: string; description: string }> = {
  high: {
    label: 'High',
    color: 'text-green-700 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    description: 'High confidence - Auto-approved',
  },
  medium: {
    label: 'Medium',
    color: 'text-yellow-700 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
    description: 'Medium confidence - Suggested categorization',
  },
  low: {
    label: 'Low',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800',
    description: 'Low confidence - Needs review',
  },
  'very-low': {
    label: 'Very Low',
    color: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    description: 'Very low confidence - Manual decision required',
  },
  none: {
    label: 'N/A',
    color: 'text-muted-foreground',
    bgColor: 'bg-muted border-muted',
    description: 'No confidence score available',
  },
}

const SIZE_CONFIG = {
  sm: 'text-xs px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
  lg: 'text-sm px-2.5 py-1',
}

export function ConfidenceBadge({
  score,
  showPercentage = true,
  showIcon = false,
  size = 'md',
  className,
}: ConfidenceBadgeProps) {
  const level = getConfidenceLevel(score)
  const config = CONFIDENCE_CONFIG[level]
  const sizeClass = SIZE_CONFIG[size]

  // Format percentage
  const percentage = score !== null && score !== undefined
    ? (score > 1 ? score : score * 100).toFixed(0)
    : null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center gap-1 rounded-full border font-medium',
              config.bgColor,
              config.color,
              sizeClass,
              className
            )}
          >
            {showIcon && <Sparkles className="h-3 w-3" />}
            {showPercentage && percentage !== null ? (
              <span>{percentage}%</span>
            ) : (
              <span>{config.label}</span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-sm">
            <p className="font-medium">{config.description}</p>
            {percentage !== null && (
              <p className="text-muted-foreground">Confidence: {percentage}%</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default ConfidenceBadge

